import React, {memo, useCallback, useEffect, useMemo, useState} from "react"
import {Bar} from "react-chartjs-2"
import {Chart, ArcElement, Tooltip, Legend, Colors} from "chart.js/auto"
import {useHistory} from "react-router-dom"
import {Container} from "../../../components/Container"
import {useRPC} from "../../../hooks/rpc"
import {EnergyModel} from "../../../store/control/state"
import {onClickHandler} from "../../../utils/onclick"
import {PhaseField} from "../components/PhaseField"
import {barDatas, barOptions} from "../settings/options"
import {calculateWeekDay, sum} from "../utils/calculate"
import useStaticData from "../data/static"
import timeConverter from "../utils/time"
import {useDispatch, useSelector} from "react-redux"
import {backButtonToggle} from "../../../store/ui/actions"
import {PhaseModel} from "../types/types"
import "./index.scss"

Chart.register(ArcElement, Tooltip, Legend, Colors)
Chart.defaults.color = "#fff"
Chart.defaults.borderColor = "rgb(136, 136, 136)"
Chart.defaults.font.size = 8

const BACK_URL = "/energy"
const date = new Date()
export const EnergyConsumptionAmount = memo(function EnergyConsumptionAmount() {
    const [rpc] = useRPC()
    const [barChartLabel, setBarChartLabel] = useState("hour")
    const [energyHourly, setEnergyHourly] = useState<EnergyModel[]>([])
    const [energyDaily, setEnergyDaily] = useState<EnergyModel[]>([])
    const [energyMonthly, setEnergyMonthly] = useState<EnergyModel[]>([])
    const controls = useSelector(state => state.control.controlList)
    const controlList = controls.filter(control => control.type_id === "200")
    const energyControlList = useSelector(state => state.control.energyState)
    const history = useHistory()
    const dispatch = useDispatch()
    const {hours, days, months} = useStaticData()
    dispatch(backButtonToggle({is_open: true, to: "/energy"}))

    const returnedFilterMonths: number[] = useMemo(() => {
        const data = []
        const findValue = (month: number) =>
            energyMonthly.filter(value => value?.month === month).find(total => total.total)?.total
        for (let j = 1; j <= 12; j++) {
            data.push({
                month: j,
                total: findValue(j) || 0
            })
        }
        return data.map(value => value?.total)
    }, [energyMonthly])

    const returnedFilterDays: number[] = useMemo(() => {
        const monthDayCount = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
        const getAllDate = (day: number) => new Date(date.getFullYear(), date.getMonth(), day).getDay()
        const data = []
        const findValue = (day: number) =>
            energyDaily.filter(value => value?.day === day).find(total => total.total)?.total

        const pastDays = monthDayCount - date.getDate()
        const followingDays = monthDayCount - pastDays
        for (let j = 1; j <= followingDays; j++) {
            data.push({
                week: getAllDate(j),
                day: j,
                total: findValue(j) || 0
            })
        }

        return data.map(value => value.total).slice(calculateWeekDay())
    }, [energyDaily])

    const returnedFilterHours: number[] = useMemo(() => {
        const today = date.getDate()
        const findValue = (hour: number) =>
            energyHourly
                .filter(value => Number(timeConverter(value?.time).day) === today && Number(timeConverter(value?.time).hour) === hour)
                .map(value => value.value)
                .find(total => total)
        const data = []
        for (let j = 0; j <= 23; j++) {
            data.push({
                value: findValue(j) || 0,
                time: j
            })
        }
        return data.map(data => data?.value)
    }, [energyHourly])

    const barChartLabels: string[] = useMemo(() => {
        if (barChartLabel === "hour") {
            return hours
        } else if (barChartLabel === "day") {
            return days
        } else if (barChartLabel === "month") {
            return months
        }
    }, [barChartLabel, hours, days, months])
    const barChartData: number[] = useMemo(() => {
        if (barChartLabel === "hour") {
            return returnedFilterHours
        } else if (barChartLabel === "day") {
            return returnedFilterDays
        } else if (barChartLabel === "month") {
            return returnedFilterMonths
        }
    }, [barChartLabel, returnedFilterHours, returnedFilterMonths, returnedFilterDays])

    const barChartDatas = barDatas(barChartLabels, barChartData, ["rgb(7, 255, 40)"])
    const [amountValue, setAmountValue] = useState(barChartData.reduce(sum, 0) || 0)

    const onClickAmountStatus = useCallback(
        (action: number) => {
            let newChartLabel = barChartLabel
            let newChartAmount = amountValue
            if (action === 1) {
                newChartLabel = "hour"
            } else if (action === 2) {
                newChartLabel = "day"
            } else if (action === 3) {
                newChartLabel = "month"
            }
            setBarChartLabel(newChartLabel)
            if (newChartLabel === "hour") {
                newChartAmount = barChartData.reduce(sum, 0)
            } else if (newChartLabel === "day") {
                newChartAmount = barChartData.reduce(sum, 0)
            } else if (newChartLabel === "month") {
                newChartAmount = barChartData.reduce(sum, 0)
            }
            setAmountValue(newChartAmount)
        },
        [barChartLabel, amountValue, barChartData]
    )
    useEffect(() => setAmountValue(barChartData.reduce(sum, 0)), [barChartData])

    const Phases: PhaseModel[] = useMemo(() => controlList, [controlList])
    const [selectedPhase, setSelectedPhase] = useState({
        id: Phases[0]?.id || undefined,
        selected: true
    })
    const onClickPhaseChanging = useCallback(
        (id: string) => {
            Phases.find(
                phase =>
                    phase.id === id &&
                    setSelectedPhase({
                        id: id,
                        selected: true
                    })
            )
        },
        [Phases]
    )
    /*const onClickAllPhase = useCallback(
        () =>
            setSelectedPhase({
                id: undefined,
                selected: false
            }),
        []
    )*/

    const amountType = useMemo(() => {
        switch (energyControlList.currency_unit) {
            case "try":
                return "TRY"
            case "usd":
                return "USD"
            case "eur":
                return "EUR"
            case "gbp":
                return "GBP"
        }
    }, [energyControlList])

    useEffect(() => {
        rpc.GetEnergyMonitorHourly({control_id: selectedPhase?.id}).then(data => setEnergyHourly(data || []))
        rpc.GetEnergyMonitorDaily({control_id: selectedPhase?.id}).then(data => setEnergyDaily(data || []))
        rpc.GetEnergyMonitorMonthly({control_id: selectedPhase?.id}).then(data => setEnergyMonthly(data || []))
    }, [rpc, selectedPhase])

    return (
        <Container>
            <div className="page-wrapper">
                <div className="energy-wrapper">
                    <div className="energy-page-container">
                        <PhaseField
                            selectedPhase={selectedPhase}
                            //onClickAllPhase={onClickAllPhase}
                            onClickPhaseChanging={onClickPhaseChanging}
                        />
                    </div>
                    <div className="energy-page-full-screen-container">
                        <div className="energy-page-full-screen">
                            <span className="energy-top-action">
                                <span className="energy-top-action-text">Tutar Grafiği ({amountType})</span>
                                <span className="energy-top-action-button">
                                    <button
                                        {...onClickHandler(() => onClickAmountStatus(1))}
                                        className={`${barChartLabel === "hour" ? "energy-active-button" : ""}`}
                                    >
                                        Saat
                                    </button>
                                    <button
                                        {...onClickHandler(() => onClickAmountStatus(2))}
                                        className={`${barChartLabel === "day" ? "energy-active-button" : ""}`}
                                    >
                                        Gün
                                    </button>
                                    <button
                                        {...onClickHandler(() => onClickAmountStatus(3))}
                                        className={`${barChartLabel === "month" ? "energy-active-button" : ""}`}
                                    >
                                        Ay
                                    </button>
                                </span>
                            </span>
                            <div className="fullscreen-bar-chart" onDoubleClick={() => history.push(BACK_URL)}>
                                <Bar data={barChartDatas} options={barOptions} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Container>
    )
})
